import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Search, ShieldCheck, AlertTriangle, X, Save } from 'lucide-react';
import RightDrawer from '../../../shared/components/form/RightDrawer';
import { Role } from '../../../types/role';
import RoleService from '../../../services/roleService';
import { usePermissionsConfig } from '../../../hooks/usePermissionsConfig';
import { BRAND_COLORS } from '../../../shared/constants/brandColors';
import PermissionMatrix from './PermissionMatrix';
import {
  sanitizePermissions,
  summarizeStripped,
} from '../utils/sanitizePermissions';

type DeptKey = 'OMS' | 'AMS' | 'EPC';

interface PermissionEditorDrawerProps {
  open: boolean;
  onClose: () => void;
  role: Role | null;
  /** Department fallback when role doesn't carry one. Defaults to OMS. */
  defaultDepartment?: DeptKey;
  onSaved?: () => void;
}

function clonePermissions(p: Role['permissions'] | undefined): Record<string, string[]> {
  if (!p) return {};
  const next: Record<string, string[]> = {};
  for (const [k, v] of Object.entries(p)) {
    if (Array.isArray(v)) next[k] = [...v];
  }
  return next;
}

function permissionsEqual(
  a: Record<string, string[]>,
  b: Record<string, string[]>,
): boolean {
  const ak = Object.keys(a).filter(k => (a[k]?.length ?? 0) > 0);
  const bk = Object.keys(b).filter(k => (b[k]?.length ?? 0) > 0);
  if (ak.length !== bk.length) return false;
  for (const k of ak) {
    const av = [...(a[k] ?? [])].sort();
    const bv = [...(b[k] ?? [])].sort();
    if (av.length !== bv.length) return false;
    for (let i = 0; i < av.length; i++) if (av[i] !== bv[i]) return false;
  }
  return true;
}

function countPermissions(p: Record<string, string[]>): {
  modules: number;
  actions: number;
} {
  let modules = 0;
  let actions = 0;
  for (const k of Object.keys(p)) {
    const list = p[k] ?? [];
    if (list.length > 0) {
      modules += 1;
      actions += list.length;
    }
  }
  return { modules, actions };
}

const PermissionEditorDrawer: React.FC<PermissionEditorDrawerProps> = ({
  open,
  onClose,
  role,
  defaultDepartment = 'OMS',
  onSaved,
}) => {
  const dept = (role?.department ?? defaultDepartment) as DeptKey;
  const isDefaultRole = !!role?.isDefault;
  const isMobileUserRole = !!role?.isMobileUserRole;

  const {
    data: config,
    isLoading: isLoadingConfig,
    error: configError,
  } = usePermissionsConfig(dept);

  const [permissions, setPermissions] = useState<Record<string, string[]>>({});
  const [search, setSearch] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Reset local state whenever a new role is loaded into the drawer.
  useEffect(() => {
    if (open && role) {
      setPermissions(clonePermissions(role.permissions));
      setSearch('');
    }
  }, [open, role]);

  const initialPermissions = useMemo(
    () => clonePermissions(role?.permissions),
    [role],
  );

  const isDirty = useMemo(
    () => !permissionsEqual(permissions, initialPermissions),
    [permissions, initialPermissions],
  );

  const counts = useMemo(() => countPermissions(permissions), [permissions]);
  const initialCounts = useMemo(() => countPermissions(initialPermissions), [
    initialPermissions,
  ]);

  const readOnly = isDefaultRole || isMobileUserRole;

  const handleSave = async () => {
    if (!role) return;
    if (readOnly) {
      toast.info(
        isDefaultRole
          ? `"${role.name}" is a default role — permissions cannot be changed.`
          : `"${role.name}" is a mobile-user role — permissions are not editable.`,
      );
      return;
    }
    const roleId = role.id || role._id;
    if (!roleId) {
      toast.error('Role ID missing — cannot save.');
      return;
    }
    if (!config) {
      toast.error('Permissions config still loading — please try again in a moment.');
      return;
    }

    // Strict sanitisation: never send a (module, action) pair the backend
    // hasn't declared. This makes the 422 "Action X is not declared on
    // module Y" failure mode impossible regardless of what historical
    // grants the role carries.
    const { clean, stripped } = sanitizePermissions(permissions, config);

    setIsSaving(true);
    try {
      await RoleService.updateRole(roleId, { permissions: clean });
      // Keep local state in sync with what was actually saved.
      setPermissions(clean);
      if (stripped.length > 0) {
        toast.success(
          `Permissions saved for "${role.name}". Cleaned up ${stripped.length} stale grant${
            stripped.length === 1 ? '' : 's'
          }: ${summarizeStripped(stripped)}`,
          { autoClose: 6000 },
        );
      } else {
        toast.success(`Permissions updated for "${role.name}"`);
      }
      onSaved?.();
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update permissions');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setPermissions(clonePermissions(role?.permissions));
  };

  if (!role) {
    return null;
  }

  return (
    <RightDrawer
      open={open}
      onClose={onClose}
      title=""
      size="3xl"
      footer={
        <Footer
          counts={counts}
          initialCounts={initialCounts}
          isDirty={isDirty}
          isSaving={isSaving}
          readOnly={readOnly}
          onCancel={onClose}
          onReset={handleReset}
          onSave={handleSave}
        />
      }
    >
      <div className="space-y-5 -mt-2">
        {/* Header card */}
        <div className="flex items-start gap-4 rounded-xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-4 shadow-sm">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center border flex-shrink-0"
            style={{
              backgroundColor: `${BRAND_COLORS.primary}15`,
              borderColor: `${BRAND_COLORS.primary}40`,
            }}
          >
            <ShieldCheck size={22} style={{ color: BRAND_COLORS.primary }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-gray-900 truncate">
                {role.name}
              </h2>
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wider"
                style={{
                  backgroundColor: `${BRAND_COLORS.primary}15`,
                  color: BRAND_COLORS.primary,
                  border: `1px solid ${BRAND_COLORS.primary}40`,
                }}
              >
                {dept}
              </span>
              {isDefaultRole && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 border border-amber-200">
                  Default
                </span>
              )}
              {!role.isActive && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-700 border border-red-200">
                  Inactive
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">
              {role.description || (
                <span className="italic text-gray-400">No description provided</span>
              )}
            </p>
            <div className="mt-2 flex items-center gap-3 text-[11px] text-gray-500">
              <span>
                <strong className="text-gray-700">{counts.modules}</strong> module
                {counts.modules === 1 ? '' : 's'}
              </span>
              <span className="text-gray-300">·</span>
              <span>
                <strong className="text-gray-700">{counts.actions}</strong>{' '}
                permission{counts.actions === 1 ? '' : 's'} granted
              </span>
              {isDirty && (
                <>
                  <span className="text-gray-300">·</span>
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700"
                  >
                    Unsaved changes
                  </span>
                </>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Read-only notice */}
        {readOnly && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
            <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900 leading-relaxed">
              {isDefaultRole && (
                <span>
                  This is a <strong>system default role</strong>. Its permissions are
                  managed by the system and cannot be changed from this screen.
                </span>
              )}
              {isMobileUserRole && (
                <span>
                  This is a <strong>mobile-user role</strong> — it doesn't use the
                  permission matrix.
                </span>
              )}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search modules…"
              className="w-full pl-9 pr-9 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
              >
                ✕
              </button>
            )}
          </div>
          <span className="text-[11px] text-gray-500 ml-auto">
            Tick boxes to grant; untick to revoke. Use row/column checkboxes to
            select all in a row/column.
          </span>
        </div>

        {/* Matrix */}
        {isLoadingConfig ? (
          <div className="rounded-lg border border-gray-200 p-6 animate-pulse space-y-2">
            <div className="h-4 bg-gray-100 rounded w-1/3" />
            <div className="h-3 bg-gray-100 rounded w-2/3" />
            <div className="h-48 bg-gray-50 rounded" />
          </div>
        ) : configError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            Failed to load permissions configuration. Please try again.
          </div>
        ) : !config || config.modules.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-500">
            No modules available for {dept}.
          </div>
        ) : (
          <PermissionMatrix
            value={permissions}
            onChange={readOnly ? () => {} : setPermissions}
            config={config}
            disabled={readOnly}
            search={search}
          />
        )}
      </div>
    </RightDrawer>
  );
};

// ─── Footer ────────────────────────────────────────────────────────────────

function Footer({
  counts,
  initialCounts,
  isDirty,
  isSaving,
  readOnly,
  onCancel,
  onReset,
  onSave,
}: {
  counts: { modules: number; actions: number };
  initialCounts: { modules: number; actions: number };
  isDirty: boolean;
  isSaving: boolean;
  readOnly: boolean;
  onCancel: () => void;
  onReset: () => void;
  onSave: () => void;
}) {
  const delta = counts.actions - initialCounts.actions;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3">
      <div className="text-xs text-gray-600">
        <span className="font-semibold text-gray-800">{counts.actions}</span>{' '}
        permission{counts.actions === 1 ? '' : 's'} selected
        {isDirty && (
          <>
            <span className="text-gray-300 mx-2">·</span>
            <span
              className={`font-semibold ${
                delta >= 0 ? 'text-emerald-700' : 'text-red-600'
              }`}
            >
              {delta >= 0 ? `+${delta}` : delta}
            </span>{' '}
            change{Math.abs(delta) === 1 ? '' : 's'}
          </>
        )}
      </div>
      <div className="flex items-center gap-2">
        {isDirty && !readOnly && (
          <button
            type="button"
            onClick={onReset}
            disabled={isSaving}
            className="px-3 py-2 text-xs font-semibold rounded-lg text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-colors disabled:opacity-50"
          >
            Reset
          </button>
        )}
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          className="px-3 py-2 text-xs font-semibold rounded-lg text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-colors disabled:opacity-50"
        >
          {readOnly || !isDirty ? 'Close' : 'Cancel'}
        </button>
        {!readOnly && (
          <button
            type="button"
            onClick={onSave}
            disabled={isSaving || !isDirty}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg text-white border border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow"
            style={{
              background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`,
            }}
          >
            {isSaving ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Save size={14} />
                Save permissions
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

export default PermissionEditorDrawer;
